// Production browser bridge. Replaces createMockBridge when running
// in a real browser (Chrome / Safari / Firefox at app.hearthcmd.{dev,com}).
//
// Differs from mock.ts in three ways:
//   1. Real app.info — git sha injected at build time via VITE_GIT_SHA,
//      platform identifies as 'web' so X-Hearth-Client + the WS upgrade
//      route through the (future) HEARTH_MIN_WEB version gate.
//   2. Real lifecycle events — document.visibilitychange / pagehide /
//      pageshow drive app.foreground / app.background emits, matching
//      the native shells' UN didEnterForeground / didEnterBackground.
//   3. Best-effort haptics via navigator.vibrate (silently no-op on
//      Safari and iOS Web, fires real buzz on Android Chrome).
//
// Native-only methods (StoreKit, push registration, biometric, voice,
// device attestation) return their existing "unavailable" shapes —
// matches mock.ts's pattern and means UI callers that already check
// `supported`/`authorized` work without a per-method gate. UI gating
// for the buttons that lead to these flows lives in the React code.

import type { Bridge } from './index'
import type { BridgeMethods } from './types'
import { createEmitter } from './emitter'

const KEYCHAIN_PREFIX = 'hearth.keychain'
const keychainKey = (key: string, sync: boolean) =>
  `${KEYCHAIN_PREFIX}.${sync ? 'sync' : 'local'}.${key}`

// VITE_GIT_SHA is injected at build time (see vite.config.ts). Empty
// in test runs or when the build couldn't resolve a sha — fall back
// to a stable marker so X-Hearth-Client always has a value.
const BUILD_VERSION: string =
  ((import.meta.env as Record<string, string | undefined>)?.VITE_GIT_SHA) ||
  '0.0.0-dev'

const BUILD_CONFIG: 'debug' | 'release' | 'appstore' = import.meta.env?.PROD
  ? 'release'
  : 'debug'

// ---- Web Push subscription helpers --------------------------------------
// Co-located with web.ts (not split into a separate module) so the bridge
// has no cross-module dependency for what should be a self-contained
// browser capability. The only thing it shares with the rest of the app
// is the localStorage keychain prefix.

const PUBLIC_KEY_PATH = '/webpush/public-key'
const TOKEN_PATH = '/token'

/// Read a stored credential from the localStorage-backed keychain.
/// Direct read (not through the bridge) so the bridge implementation
/// doesn't recursively dispatch into itself for what is essentially a
/// local lookup.
function readCred(key: string, sync: boolean): string | null {
  return localStorage.getItem(`hearth.keychain.${sync ? 'sync' : 'local'}.${key}`)
}

/// RFC 8292 VAPID public keys arrive as base64url; PushManager.subscribe
/// wants a BufferSource. Standard helper recipe — pad, swap URL-safe
/// chars, then decode. Constructs the underlying ArrayBuffer explicitly
/// (rather than letting Uint8Array's default ArrayBufferLike type
/// leak SharedArrayBuffer in TS 5.7+'s narrower lib types).
function urlBase64ToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4)
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const buf = new ArrayBuffer(raw.length)
  const out = new Uint8Array(buf)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

async function fetchVAPIDPublicKey(): Promise<string> {
  const res = await fetch(PUBLIC_KEY_PATH, { method: 'GET' })
  if (!res.ok) throw new Error(`public-key fetch failed: ${res.status}`)
  const body: { public_key?: string } = await res.json()
  if (!body.public_key) throw new Error('public-key response missing key')
  return body.public_key
}

async function postSubscriptionToServer(sub: PushSubscription): Promise<void> {
  // Key names must match api/keychain.ts (camelCase), not the snake_case
  // wire shape. An earlier version of this file used the wire names and
  // silently no-op'd, leaving the server unaware of the subscription.
  const ioDeviceID = readCred('ioDeviceID', false)
  const secret = readCred('ioDeviceSecret', false)
  if (!ioDeviceID || !secret) {
    // Not enrolled yet. Throw rather than silently succeed so the
    // caller's catch surfaces a real error in the UI; otherwise
    // permission-granted lights up green and there's no on-device
    // signal that the server doesn't know about us.
    throw new Error('cannot register web push: device not enrolled')
  }
  // Server-side validateWebPushSubscriptionJSON accepts the raw
  // PushSubscription.toJSON() shape: {endpoint, keys: {p256dh, auth}}.
  // Re-shape to the flat {endpoint, p256dh, auth} the Go side expects.
  const raw = sub.toJSON()
  const keys = (raw.keys || {}) as { p256dh?: string; auth?: string }
  const payload = {
    io_device_id: ioDeviceID,
    secret,
    platform: 'webpush',
    apns_token: JSON.stringify({
      Endpoint: raw.endpoint,
      P256dh: keys.p256dh ?? '',
      Auth: keys.auth ?? '',
    }),
    is_sandbox: false,
  }
  const res = await fetch(TOKEN_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`/token POST failed: ${res.status} ${text}`)
  }
  // Loud success line for the DevTools console — Web Push debugging is
  // miserable enough without explicit confirmation that the subscription
  // round-tripped. Cheap, removable later.
  console.info('webpush: subscription registered with server')
}

async function registerForWebPush(): Promise<boolean> {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false
  }
  if (typeof Notification === 'undefined') return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js')
    // Wait for the SW to be ready (some browsers register before activation
    // completes; subscribe() needs an active controller).
    await navigator.serviceWorker.ready

    const existing = await reg.pushManager.getSubscription()
    if (existing) {
      // Already subscribed in this browser — re-POST to server so a
      // newly-enrolled io_device picks up the existing subscription.
      await postSubscriptionToServer(existing)
      return true
    }

    const publicKey = await fetchVAPIDPublicKey()
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    })
    await postSubscriptionToServer(sub)
    return true
  } catch (err) {
    // Common failure modes: missing service worker scope, fetch failure
    // for public key, push service rejection. Log + return false; caller
    // already gates UI on the boolean.
    console.warn('webpush: registration failed', err)
    return false
  }
}

export function createWebBridge(): Bridge {
  const emitter = createEmitter()

  // Lifecycle: visibilitychange covers tab-hide / tab-show in normal
  // navigation. pagehide / pageshow catch bfcache restores (back/forward
  // nav) where visibilitychange doesn't fire on every browser. Both
  // listeners idempotently emit; downstream subscribers tolerate dupes.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') emitter.emit('app.foreground', {})
      else emitter.emit('app.background', {})
    })
    window.addEventListener('pageshow', () => emitter.emit('app.foreground', {}))
    window.addEventListener('pagehide', () => emitter.emit('app.background', {}))
  }

  // Push-tap routing — two paths for cold-launch vs. warm.
  //
  // Cold (no Hearth tab open): notificationclick in the SW
  // openWindow's `/?push_request_id=...&push_position_id=...`. Parse
  // those out at construction time and emit push.tapped so ws.ts's
  // listener navigates to the requesting agent. queueMicrotask defers
  // until after the bridge module's pending-events drain so listeners
  // are registered.
  //
  // Warm (Hearth tab already open): notificationclick instead does
  // client.postMessage({type:'push.tapped', ...}). Listen for those
  // here and forward as a bridge emit so the same downstream handler
  // path fires regardless of cold vs warm.
  if (typeof window !== 'undefined') {
    queueMicrotask(() => {
      const params = new URLSearchParams(window.location.search)
      const requestID = params.get('push_request_id') || undefined
      const positionID = params.get('push_position_id') || undefined
      const orgID = params.get('push_org_id') || undefined
      if (requestID || positionID) {
        emitter.emit('push.tapped', {
          requestID,
          organizationPositionID: positionID,
          organizationID: orgID,
          raw: { source: 'cold_launch_url' },
        })
        // Strip the params from the URL so a reload doesn't refire.
        params.delete('push_request_id')
        params.delete('push_position_id')
        params.delete('push_org_id')
        const cleaned =
          window.location.pathname +
          (params.toString() ? `?${params.toString()}` : '') +
          window.location.hash
        window.history.replaceState(null, '', cleaned)
      }
      // Invitation deep link: /invitations[?email=<addr>]. The legacy
      // /accept?t=<token> URL is no longer recognized — the token
      // mechanism is gone. `email`, when present, is a prefill hint for
      // the registration screen; it names the invited mailbox but
      // authorizes nothing (the OTP still proves ownership).
      if (window.location.pathname === '/invitations') {
        const email = params.get('email') ?? undefined
        emitter.emit('invite.link', email ? { email } : {})
        params.delete('email')
        const cleaned =
          window.location.pathname +
          (params.toString() ? `?${params.toString()}` : '') +
          window.location.hash
        window.history.replaceState(null, '', cleaned)
      }
      // Display pairing deep link: /pair?code=<6-digit> (from scanning a kiosk QR).
      // Hand the code to the store; App claims it once the user is enrolled.
      if (window.location.pathname === '/pair') {
        const code = params.get('code') ?? undefined
        if (code && /^\d{6}$/.test(code)) emitter.emit('pair.link', { code })
        params.delete('code')
        const cleaned =
          '/' + (params.toString() ? `?${params.toString()}` : '') + window.location.hash
        window.history.replaceState(null, '', cleaned)
      }
    })
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const data = (event.data ?? {}) as {
          type?: string
          requestID?: string
          organizationPositionID?: string
          organizationID?: string
        }
        if (data.type !== 'push.tapped') return
        emitter.emit('push.tapped', {
          requestID: data.requestID,
          organizationPositionID: data.organizationPositionID,
          organizationID: data.organizationID,
          raw: { source: 'sw_message' },
        })
      })
    }
  }

  const dispatch = async <K extends keyof BridgeMethods>(
    method: K,
    args: BridgeMethods[K]['args'],
  ): Promise<BridgeMethods[K]['result']> => {
    switch (method) {
      case 'keychain.get': {
        const a = args as BridgeMethods['keychain.get']['args']
        const value = localStorage.getItem(keychainKey(a.key, a.sync))
        return { value } as BridgeMethods[K]['result']
      }
      case 'keychain.set': {
        const a = args as BridgeMethods['keychain.set']['args']
        localStorage.setItem(keychainKey(a.key, a.sync), a.value)
        return {} as BridgeMethods[K]['result']
      }
      case 'keychain.delete': {
        const a = args as BridgeMethods['keychain.delete']['args']
        localStorage.removeItem(keychainKey(a.key, a.sync))
        return {} as BridgeMethods[K]['result']
      }

      // Device attestation: there's no browser equivalent of DeviceCheck
      // or Play Integrity. Report unsupported so the enroll flow sends
      // attestation type=none. Server's dev build accepts none for any
      // platform (acceptsNoneAttestation); prod web policy is TBD per
      // docs/web-as-product-surface.md.
      case 'devicecheck.token':
        return {
          token: null,
          isSandbox: false,
          supported: false,
        } as BridgeMethods[K]['result']
      case 'playintegrity.token':
        return {
          token: null,
          supported: false,
        } as BridgeMethods[K]['result']

      // Web Push subscription flow. Mirrors the native APNs path that
      // returns {authorized} after permission + registration. Steps:
      //   1. Feature-detect ServiceWorker + PushManager. Safari < 16 and
      //      iOS Safari outside a PWA lack PushManager → authorized:false.
      //   2. Notification.requestPermission(). Denied or default → bail.
      //   3. Register service-worker.js (idempotent — same URL = same SW).
      //   4. Reuse an existing subscription if present; otherwise fetch
      //      the VAPID public key and call pushManager.subscribe.
      //   5. POST the subscription to /token (same endpoint APNs/FCM use)
      //      so the server-side fanout can route to this browser.
      case 'apns.registerForPush': {
        const ok = await registerForWebPush()
        return { authorized: ok } as BridgeMethods[K]['result']
      }

      // StoreKit / Play Billing: no browser equivalent. Pro via Stripe
      // (or similar) is Phase 3. Until then, every storekit call
      // resolves with the "not subscribed locally" answer and the UI
      // points users at the iOS/Android app to subscribe.
      case 'storekit.products':
        return { products: [] } as BridgeMethods[K]['result']
      case 'storekit.purchase':
        return {
          outcome: 'failed',
          error: 'unavailable_in_browser',
        } as BridgeMethods[K]['result']
      case 'storekit.restore':
        return { restored: false } as BridgeMethods[K]['result']
      case 'storekit.status':
        return {
          subscribed: false,
          productID: null,
          expiresAt: null,
        } as BridgeMethods[K]['result']
      case 'storekit.latestTransactionJWS':
        return { jws: null } as BridgeMethods[K]['result']

      // Voice (WhisperKit). Browser equivalent is the Web Speech API but
      // it's not wired here. supported=false hides the mic button.
      case 'whisper.isAvailable':
        return {
          supported: false,
          ready: false,
          needsDownload: false,
          downloading: false,
          loading: false,
          reason: 'voice input requires the iOS or Android app',
        } as BridgeMethods[K]['result']
      case 'whisper.prepare':
        return {
          ready: false,
          error: 'unavailable_in_browser',
        } as BridgeMethods[K]['result']
      case 'whisper.startRecording':
        return {} as BridgeMethods[K]['result']
      case 'whisper.stopRecording':
        return { transcript: '' } as BridgeMethods[K]['result']

      // Haptics — best-effort via the Vibration API. Chrome on Android
      // fires real buzz; Safari/iOS-Web ignores the call. Different
      // patterns per type loosely mirror the native UIImpactFeedback
      // generators.
      case 'haptic.light':
        navigator.vibrate?.(10)
        return {} as BridgeMethods[K]['result']
      case 'haptic.success':
        navigator.vibrate?.([10, 30, 10])
        return {} as BridgeMethods[K]['result']
      case 'haptic.warning':
        navigator.vibrate?.([20, 40, 20])
        return {} as BridgeMethods[K]['result']

      case 'bridge.ready':
      case 'notifications.reconcile':
        // No native NotificationCenter in the browser; the call is a
        // no-op here. Phase 2 (Web Push) will revisit notifications.reconcile
        // if browser notifications start needing reconciliation.
        return {} as BridgeMethods[K]['result']

      case 'notifications.clearForTarget':
        // Same story — no native tray to clear. Distinct case only
        // because its result shape carries a `removed` count.
        return { removed: 0 } as BridgeMethods[K]['result']

      case 'app.info':
        return {
          version: BUILD_VERSION,
          build: BUILD_VERSION,
          // Stable, env-agnostic bundle ID for the web client. The dev
          // vs prod distinction comes from the page's hostname, not
          // from this field.
          bundleID: 'com.vergelabs.hearthcmd.web',
          gitSHA: BUILD_VERSION,
          buildConfig: BUILD_CONFIG,
          platform: 'web',
        } as BridgeMethods[K]['result']

      case 'app.openURL':
        window.open((args as { url: string }).url, '_blank', 'noopener')
        return { ok: true } as BridgeMethods[K]['result']

      case 'clipboard.write': {
        // No pasteboard scoping to be had outside a native shell, so this
        // tier is just the async Clipboard API. It needs a secure context
        // and transient user activation; util/clipboard.ts owns the
        // execCommand fallback for when it has neither.
        const text = (args as { text: string }).text
        if (!navigator.clipboard?.writeText) {
          return { ok: false } as BridgeMethods[K]['result']
        }
        await navigator.clipboard.writeText(text)
        return { ok: true } as BridgeMethods[K]['result']
      }

      case 'files.open':
        // A plain browser tab has no OS file handoff; the viewer keys off
        // isNative (false here) and offers a download link instead.
        return { ok: false } as BridgeMethods[K]['result']

      case 'voice.capabilities':
        // Native-only, per this file's stance on voice. A browser tab does have
        // speechSynthesis, and wiring it up would be a few lines — but the
        // hands-free design is phone-shaped (§2), and a page that starts talking
        // is a bigger promise than "we could call the API". Report honestly and
        // leave the door open.
        return {
          speak: false,
          session: false,
          onDeviceRecognition: false,
          reason: 'browser shell — voice not implemented',
        } as BridgeMethods[K]['result']
      case 'voice.speak':
        return { speaking: false } as BridgeMethods[K]['result']
      case 'voice.stopSpeaking':
      case 'voice.setKeepAwake':
      case 'voice.endSession':
        // No session to keep awake here, and the Screen Wake Lock API would be
        // a promise this bridge can't keep across tabs. Inert.
        return {} as BridgeMethods[K]['result']
      case 'voice.startSession':
        return { started: false } as BridgeMethods[K]['result']

      default: {
        const _exhaustive: never = method
        throw new Error(`web bridge: unknown method ${String(_exhaustive)}`)
      }
    }
  }

  return {
    isNative: false,
    isAndroid: false,
    call(method, args) {
      return dispatch(method, args ?? ({} as never))
    },
    on: emitter.on,
    emit: emitter.emit,
  }
}
